import { useQuery } from '@tanstack/react-query';
import type { CampaignDto, HomeBannerDto, SponsoredCampaignDto } from '@rw/shared';
import { useMemo } from 'react';
import { api } from '../../lib/api';

export const campaignKeys = {
  admin: ['admin', 'campaigns'] as const,
  cells: (regionId: string) => ['sponsored-cells', regionId] as const,
  banner: (regionId: string) => ['home-banner', regionId] as const,
};

/** Running campaigns' sponsored cells in a city, keyed by grid key. */
export function useSponsoredCells(regionId: string | undefined) {
  const q = useQuery({
    queryKey: campaignKeys.cells(regionId ?? ''),
    queryFn: () => api<SponsoredCampaignDto[]>(`/sponsored-cells?regionId=${encodeURIComponent(regionId!)}`),
    enabled: !!regionId,
    staleTime: 5 * 60_000,
  });
  const byCell = useMemo(() => {
    const m = new Map<string, SponsoredCampaignDto>();
    for (const c of q.data ?? []) for (const k of c.cells) m.set(k, c);
    return m;
  }, [q.data]);
  return { ...q, byCell };
}

export function useHomeBanner(regionId: string | undefined) {
  return useQuery({
    queryKey: campaignKeys.banner(regionId ?? ''),
    queryFn: async () => (await api<{ banner: HomeBannerDto | null }>(`/home-banner?regionId=${encodeURIComponent(regionId!)}`)).banner,
    enabled: !!regionId,
    staleTime: 5 * 60_000,
  });
}

/** Game owner: every campaign with its totals. */
export const useCampaigns = () => useQuery({ queryKey: campaignKeys.admin, queryFn: () => api<CampaignDto[]>('/admin/campaigns') });

// ── views and taps: counted per day on the server, never per player ──

type Placement = 'CELL' | 'BANNER';
type Kind = 'VIEW' | 'CLICK';
const queue: Array<{ campaignId: string; placement: Placement; kind: Kind }> = [];
/** A view counts once per app session per campaign and placement. */
const seen = new Set<string>();
let timer: ReturnType<typeof setTimeout> | null = null;

function flush(keepalive = false) {
  if (timer) clearTimeout(timer);
  timer = null;
  while (queue.length) {
    const events = queue.splice(0, 50);
    void api('/ad-stats', { body: { events }, keepalive }).catch(() => undefined);
  }
}

// Leaving or hiding the page sends what is waiting, so it isn't lost.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flush(true));
  window.addEventListener('pagehide', () => flush(true));
}

export function trackAd(campaignId: string, placement: Placement, kind: Kind) {
  if (kind === 'VIEW') {
    const k = `${campaignId}|${placement}`;
    if (seen.has(k)) return;
    seen.add(k);
  }
  queue.push({ campaignId, placement, kind });
  timer ??= setTimeout(() => flush(), 3000);
}
